import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { THEMES } from '../../utils/themes';
import { FOUNDING_OWNER_ID } from '../../utils/constants';

const NAV_ITEMS = [
  { path: 'overview', icon: '📊', label: 'Overview' },
  { path: 'staff', icon: '👥', label: 'Staff' },
  { path: 'shifts', icon: '⏱️', label: 'Shifts' },
  { path: 'ranks', icon: '🏷️', label: 'Ranks' },
  { path: 'payroll', icon: '💵', label: 'Payroll' },
  { path: 'bonuses', icon: '🎁', label: 'Bonuses' },
  { path: 'config', icon: '⚙️', label: 'Config' },
  { path: 'panel', icon: '🖼️', label: 'Panel Settings' },
  { path: 'logs', icon: '📋', label: 'Audit Logs' },
  { path: 'welcomer', icon: '👋', label: 'Welcomer' },
  { path: 'requests', icon: '📨', label: 'Requests' },
  { path: 'applications', icon: '📝', label: 'Applications' },
  { path: 'departments', icon: '🏢', label: 'Departments' },
];

const OWNER_NAV = [
  { path: 'keys', icon: '🔑', label: 'License Keys' },
  { path: 'partners', icon: '👑', label: 'Partners' },
  { path: 'subscription', icon: '⭐', label: 'Subscription' },
];

export default function DashboardLayout({ guild }) {
  const { guildId } = useParams();
  const { user, logout } = useAuth();
  const { themeId, serverTheme, changeTheme, changeServerTheme, resetToServerTheme, loadGuildTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Load guild-specific theme on mount
  useEffect(() => {
    if (guildId) loadGuildTheme(guildId);
  }, [guildId]);

  const [showThemePicker, setShowThemePicker] = useState(false);
  const [bgUrl, setBgUrl] = useState(() => localStorage.getItem(`bg_${guildId}`) || '');
  const [bgEnabled, setBgEnabled] = useState(() => localStorage.getItem(`bg_enabled_${guildId}`) !== 'false');
  const [bgOpacity, setBgOpacity] = useState(() => parseFloat(localStorage.getItem(`bg_opacity_${guildId}`) || '0.08'));
  const [customBgInput, setCustomBgInput] = useState('');

  const DEFAULT_BACKGROUNDS = [
    { id: 'none', name: 'None', url: '' },
    { id: 'grid', name: 'Grid', url: 'https://i.postimg.cc/placeholder1.png' },
  ];

  const handleSetBg = (url) => {
    setBgUrl(url);
    localStorage.setItem(`bg_${guildId}`, url);
  };

  const handleBgToggle = (val) => {
    setBgEnabled(val);
    localStorage.setItem(`bg_enabled_${guildId}`, val);
  };

  const handleOpacity = (val) => {
    setBgOpacity(val);
    localStorage.setItem(`bg_opacity_${guildId}`, val);
  };

  const isOwner = user?.discord_id === FOUNDING_OWNER_ID;
  const isPartner = !isOwner && (user?.bot_tier === 'partner' || user?.bot_tier === 'owner');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const currentPath = location.pathname.split('/').pop();

  const NavLink = ({ item }) => {
    const active = currentPath === item.path;
    return (
      <button
        className="nav-link-item"
        onClick={() => { navigate(`/dashboard/${guildId}/${item.path}`); setSidebarOpen(false); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: collapsed ? 0 : '12px',
          padding: collapsed ? '12px' : '12px 16px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: active ? 'var(--accent-soft)' : 'transparent',
          border: 'none', borderRadius: '10px',
          color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
          fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: active ? 700 : 500,
          cursor: 'pointer', transition: 'all 0.15s',
          borderLeft: active ? '3px solid var(--accent-primary)' : '3px solid transparent',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
        title={collapsed ? item.label : ''}
      >
        <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
        {!collapsed && <span>{item.label}</span>}
      </button>
    );
  };

  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'var(--font-body)',
      background: 'var(--bg-primary)',
      position: 'relative',
    }}>
      {/* Background image layer */}
      {bgEnabled && bgUrl && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0,
          backgroundImage: `url(${bgUrl})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: bgOpacity,
          pointerEvents: 'none',
        }} />
      )}
      {/* Sidebar overlay — mobile only */}
      {sidebarOpen && (
        <div className="sidebar-overlay" style={{ display: 'none' }} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`} style={{
        width: collapsed ? '64px' : '220px',
        background: 'var(--bg-sidebar)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0,
        zIndex: 100, transition: 'width 0.25s ease',
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{
          padding: collapsed ? '16px 12px' : '20px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'var(--gradient-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px', flexShrink: 0,
          }}>🕐</div>
          {!collapsed && (
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: '14px',
              fontWeight: 700, color: 'var(--accent-primary)',
              whiteSpace: 'nowrap',
            }}>Clock-In</span>
          )}
        </div>

        {/* Guild info */}
        {!collapsed && guild && (
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            {guild.icon ? (
              <img src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                alt={guild.name}
                style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            ) : (
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: 'var(--gradient-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', color: '#fff', fontWeight: 700,
              }}>{guild.name?.charAt(0)}</div>
            )}
            <span style={{
              fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{guild.name}</span>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {NAV_ITEMS.map(item => <NavLink key={item.path} item={item} />)}
          </div>

          {/* Owner only section */}
          {(isOwner || isPartner) && (
            <>
              {!collapsed && (
                <div style={{
                  fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '1px', color: 'var(--text-muted)',
                  padding: '16px 16px 8px',
                }}>{isOwner ? 'Owner' : 'Partner'}</div>
              )}
              {collapsed && <div style={{ margin: '12px 0', borderTop: '1px solid var(--border)' }} />}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {OWNER_NAV.map(item => <NavLink key={item.path} item={item} />)}
              </div>
            </>
          )}
        </nav>

        {/* Bottom actions */}
        <div style={{
          padding: '8px', borderTop: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', gap: '4px',
        }}>
          {/* Theme toggle */}
          <button
            onClick={() => setShowThemePicker(!showThemePicker)}
            style={{
              width: '100%', padding: '10px',
              display: 'flex', alignItems: 'center',
              gap: collapsed ? 0 : '10px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              background: 'transparent', border: 'none', borderRadius: '8px',
              cursor: 'pointer', color: 'var(--text-secondary)',
              fontSize: '13px', fontFamily: 'var(--font-body)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{ fontSize: '16px' }}>🎨</span>
            {!collapsed && <span>Theme</span>}
          </button>

          {/* Back to servers */}
          <button
            onClick={() => navigate('/servers')}
            style={{
              width: '100%', padding: '10px',
              display: 'flex', alignItems: 'center',
              gap: collapsed ? 0 : '10px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              background: 'transparent', border: 'none', borderRadius: '8px',
              cursor: 'pointer', color: 'var(--text-secondary)',
              fontSize: '13px', fontFamily: 'var(--font-body)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{ fontSize: '16px' }}>🏠</span>
            {!collapsed && <span>All Servers</span>}
          </button>

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              width: '100%', padding: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', borderRadius: '8px',
              cursor: 'pointer', color: 'var(--text-muted)',
              fontSize: '16px', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>
      </aside>

      {/* Theme picker overlay */}
      {showThemePicker && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowThemePicker(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)', borderRadius: '20px',
              padding: '28px', maxWidth: '480px', width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.20)',
              border: '1px solid var(--border)',
              maxHeight: '85vh', overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                🎨 Theme Settings
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Customize how the dashboard looks for you and your server.
              </p>
            </div>

            {/* Personal Theme Section */}
            <div style={{
              background: 'var(--bg-secondary)', borderRadius: '14px',
              padding: '16px', marginBottom: '16px',
              border: '2px solid var(--accent-primary)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    👤 My Theme
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Only <strong>you</strong> see this — does not affect other admins
                  </div>
                </div>
                {serverTheme && themeId !== serverTheme && (
                  <button onClick={() => { resetToServerTheme(guildId); setShowThemePicker(false); }}
                    style={{
                      fontSize: '11px', color: 'var(--accent-primary)', background: 'var(--bg-card)',
                      border: '1px solid var(--accent-primary)', borderRadius: '6px',
                      padding: '4px 10px', cursor: 'pointer', fontFamily: 'var(--font-body)',
                    }}>
                    ↩ Reset to server default
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {Object.values(THEMES).map(t => (
                  <button key={t.id}
                    onClick={() => { changeTheme(t.id, guildId); setShowThemePicker(false); }}
                    style={{
                      padding: '10px', borderRadius: '10px', cursor: 'pointer',
                      border: `2px solid ${themeId === t.id ? 'var(--accent-primary)' : 'transparent'}`,
                      background: themeId === t.id ? 'var(--bg-card)' : 'var(--bg-card)',
                      textAlign: 'left', fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                      position: 'relative',
                    }}>
                    {themeId === t.id && (
                      <div style={{
                        position: 'absolute', top: '6px', right: '6px',
                        width: '16px', height: '16px', borderRadius: '50%',
                        background: 'var(--accent-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '10px', color: '#fff',
                      }}>✓</div>
                    )}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '5px' }}>
                      {t.preview.map((c, i) => <div key={i} style={{ width: '14px', height: '14px', borderRadius: '50%', background: c }} />)}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-primary)' }}>{t.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Server Theme Section */}
            <div style={{
              background: 'var(--bg-secondary)', borderRadius: '14px',
              padding: '16px',
              border: '2px solid var(--accent-secondary)',
            }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  🏠 Server Default Theme
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  What <strong>all admins</strong> see by default when they open this server
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {Object.values(THEMES).map(t => (
                  <button key={t.id}
                    onClick={() => { changeServerTheme(t.id, guildId); }}
                    style={{
                      padding: '10px', borderRadius: '10px', cursor: 'pointer',
                      border: `2px solid ${serverTheme === t.id ? 'var(--accent-secondary)' : 'transparent'}`,
                      background: 'var(--bg-card)',
                      textAlign: 'left', fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                      position: 'relative',
                    }}>
                    {serverTheme === t.id && (
                      <div style={{
                        position: 'absolute', top: '6px', right: '6px',
                        width: '16px', height: '16px', borderRadius: '50%',
                        background: 'var(--accent-secondary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '10px', color: '#fff',
                      }}>✓</div>
                    )}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '5px' }}>
                      {t.preview.map((c, i) => <div key={i} style={{ width: '14px', height: '14px', borderRadius: '50%', background: c }} />)}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-primary)' }}>{t.name}</div>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>
                💡 Individual admins can override this with their own personal theme
              </div>
            </div>

            {/* Background section */}
            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>🖼️ Dashboard Background</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Image or GIF shown behind the dashboard</div>
                </div>
                <button onClick={() => handleBgToggle(!bgEnabled)} style={{
                  width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                  cursor: 'pointer', background: bgEnabled && bgUrl ? 'var(--success)' : 'var(--border)',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <div style={{
                    width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '3px',
                    left: bgEnabled && bgUrl ? '23px' : '3px',
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </button>
              </div>

              {/* Opacity slider */}
              {bgEnabled && bgUrl && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Opacity</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{Math.round(bgOpacity * 100)}%</span>
                  </div>
                  <input type="range" min="0.02" max="0.30" step="0.01"
                    value={bgOpacity}
                    onChange={e => handleOpacity(parseFloat(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--accent-primary)' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)' }}>
                    <span>Subtle</span><span>Bold</span>
                  </div>
                </div>
              )}

              {/* Custom URL input */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Custom Image or GIF URL
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="url"
                    placeholder="https://example.com/background.gif"
                    value={customBgInput}
                    onChange={e => setCustomBgInput(e.target.value)}
                    style={{
                      flex: 1, padding: '9px 12px',
                      border: '2px solid var(--border)', borderRadius: '10px',
                      fontSize: '13px', fontFamily: 'var(--font-body)',
                      background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none',
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                  <button onClick={() => { handleSetBg(customBgInput); setBgEnabled(true); localStorage.setItem(`bg_enabled_${guildId}`, true); }}
                    disabled={!customBgInput}
                    style={{
                      padding: '9px 16px', background: customBgInput ? 'var(--gradient-accent)' : 'var(--border)',
                      color: '#fff', border: 'none', borderRadius: '10px',
                      fontSize: '13px', fontWeight: 600, cursor: customBgInput ? 'pointer' : 'not-allowed',
                      fontFamily: 'var(--font-display)', flexShrink: 0,
                    }}>Apply</button>
                </div>
                {bgUrl && (
                  <button onClick={() => { handleSetBg(''); setBgEnabled(false); setCustomBgInput(''); }}
                    style={{ marginTop: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--danger)', padding: 0 }}>
                    ✕ Remove background
                  </button>
                )}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                  Supports PNG, JPG, GIF. Keep opacity low for readability. Large GIFs may slow the page.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="dashboard-main" style={{
        marginLeft: collapsed ? '64px' : '220px',
        flex: 1, transition: 'margin-left 0.25s ease',
        minHeight: '100vh',
      }}>
        {/* Top bar */}
        <div className="top-bar" style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'var(--bg-card)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border)',
          padding: '12px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Hamburger button — mobile only */}
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(o => !o)}
              style={{
                display: 'none', flexDirection: 'column', gap: '4px',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '6px', borderRadius: '6px', flexShrink: 0,
              }}
              aria-label="Toggle menu"
            >
              <span style={{ width: '20px', height: '2px', background: 'var(--accent-primary)', borderRadius: '1px', display: 'block' }} />
              <span style={{ width: '20px', height: '2px', background: 'var(--accent-primary)', borderRadius: '1px', display: 'block' }} />
              <span style={{ width: '20px', height: '2px', background: 'var(--accent-primary)', borderRadius: '1px', display: 'block' }} />
            </button>
            {/* Server icon + name */}
            {guild && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingRight: '16px', borderRight: '1px solid var(--border)' }}>
                {guild.icon ? (
                  <img
                    src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                    alt={guild.name}
                    style={{ width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                    background: 'var(--gradient-accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: 700, color: '#fff',
                  }}>{guild.name?.charAt(0)}</div>
                )}
                <span className="server-name-label" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {guild.name}
                </span>
              </div>
            )}
            {/* Current page */}
            <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              {NAV_ITEMS.find(n => n.path === currentPath)?.icon}{' '}
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                {NAV_ITEMS.find(n => n.path === currentPath)?.label ||
                 OWNER_NAV.find(n => n.path === currentPath)?.label || 'Dashboard'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {user && (
              <>
                <img
                  src={user.avatar
                    ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`
                    : `https://cdn.discordapp.com/embed/avatars/0.png`}
                  alt={user.username}
                  style={{ width: '30px', height: '30px', borderRadius: '50%', border: '2px solid var(--border)' }}
                />
                <span className="username-text" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {user.username}
                  {isOwner && <span style={{ marginLeft: '6px', fontSize: '11px', background: 'var(--gradient-accent)', color: '#fff', padding: '2px 8px', borderRadius: '100px' }}>Owner</span>}
                  {isPartner && <span style={{ marginLeft: '6px', fontSize: '11px', background: 'rgba(52,152,219,0.85)', color: '#fff', padding: '2px 8px', borderRadius: '100px' }}>Partner</span>}
                </span>
                <button onClick={logout} style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: '8px', padding: '6px 14px',
                  fontSize: '12px', cursor: 'pointer',
                  color: 'var(--text-secondary)', fontFamily: 'var(--font-body)',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.color = 'var(--danger)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                >Logout</button>
              </>
            )}
          </div>
        </div>

        {/* Page content */}
        <div className="page-content" style={{ padding: '32px' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
